import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { favoriteProduct, getProducts, postAnnotation, unFavoriteProduct } from "../../../../../services/productService";
import { clearPreselectProducts, getPreselectProducts, getQuery, setQuery } from "../../../../../services/local-session-storage";
import { toProductEntries } from "../../query.model";
import { handlePromise } from "../../../../../utils/handlePromisse";
import { useAuth } from "../../../../../contexts/auth2";
import { flexMultiFilter, orderObjectArray } from "../../../../../utils";
import { ProductModel } from "../../../../../models/Product.model";

/** Estado, fetch e handlers da etapa de seleção de ativos (produtos) do wizard tópico. */
export function useProducts(onFinish: () => void, onAddMoreItem: () => void) {
    const model = getQuery();
    const {
        user: { idUser },
    } = useAuth();

    const [loading, setLoading] = useState(false);
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [value, setValue] = useState(0);
    const [open, setOpen] = useState(false);
    const [products, setProducts] = useState<ProductModel[]>([]);
    const productsRef = useRef(products);
    productsRef.current = products;
    const [filter, setFilter] = useState("");
    const [filterKey, setFilterKey] = useState("productName");
    const [selectedProducts, setSelectedProducts] = useState<ProductModel[]>(model.products || []);
    // Espelho do estado para os handlers lerem o valor atual sem mudar de referência
    // a cada render (senão o React.memo dos cards não segura o re-render).
    const selectedRef = useRef(selectedProducts);
    selectedRef.current = selectedProducts;
    const [selectedProduct, setSelectedProduct] = useState<ProductModel>({} as ProductModel);
    const [annotationOpen, setAnnotationOpen] = useState(false);
    const [activeSelected, setActiveSelected] = useState<ProductModel | null>(null);
    const [onPopUp, setOnPopUp] = useState(false);

    const fetchProducts = useCallback(async () => {
        setLoading(true);
        const request = toProductEntries(model);
        const [result, error] = await handlePromise(getProducts(request));

        // Sem este tratamento, um erro da API deixava `result` indefinido: o acesso a
        // result.data estourava antes do setLoading(false) e a etapa ficava girando
        // para sempre, sem dizer nada para a usuária.
        if (error || !result?.data) {
            setProducts([]);
            setSnackbarMessage("Não foi possível carregar os ativos. Tente de novo.");
            setShowSnackbar(true);
            setLoading(false);
            return;
        }

        setProducts(result.data as ProductModel[]);
        setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    // Mescla ativos pré-selecionados a partir da biblioteca ("usar em nova consulta").
    useEffect(() => {
        const preselect = getPreselectProducts();
        if (preselect.length === 0) return;
        clearPreselectProducts();
        const current = getQuery().products || [];
        const merged = [...current];
        preselect.forEach((p) => {
            if (!merged.some((x) => x.idProduct === p.idProduct)) merged.push(p);
        });
        setSelectedProducts(merged);
        setQuery({ products: merged });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setFilter("");
        setValue(newValue);
    };

    const handleClickProduct = useCallback((product: ProductModel) => {
        setOpen(true);
        setSelectedProduct(product);
    }, []);

    /** Atualiza estado, ref (na hora, para cliques em sequência) e sessão. */
    const commitSelection = useCallback((next: ProductModel[]) => {
        selectedRef.current = next;
        setSelectedProducts(next);
        setQuery({ products: next });
    }, []);

    const handleSelectProduct = useCallback((product: ProductModel, checked: boolean) => {
        const current = selectedRef.current;
        if (!checked) {
            commitSelection(current.filter((prod) => prod.idProduct !== product.idProduct));
            return;
        }
        commitSelection([...current, product]);
    }, [commitSelection]);

    const handleAddMoreItems = () => {
        setQuery({ products: selectedProducts });
        onAddMoreItem();
    };

    const handleContinue = () => {
        setQuery({ products: selectedProducts });
        onFinish();
    };

    const handleClickOpen = useCallback(async (active: ProductModel) => {
        setActiveSelected(active);
        setAnnotationOpen(true);
    }, []);

    const updateProductAnnotation = (idProduct: number, annotation: string) => {
        setProducts((prevProducts) =>
            prevProducts.map((product) => (product.idProduct === idProduct ? { ...product, annotation } : product))
        );
    };

    const saveAnnotation = useCallback(
        async (newAnnotation: string) => {
            if (!activeSelected || !activeSelected.idProduct) return;

            const [_, error] = await handlePromise(postAnnotation(activeSelected?.idProduct, newAnnotation));
            if (!!error) {
                return;
            }

            updateProductAnnotation(activeSelected.idProduct, newAnnotation);
            setAnnotationOpen(false);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [activeSelected]
    );

    const popUpOpen = useCallback(async () => {
        setOnPopUp(true);
    }, []);

    const handleClose = useCallback(() => {
        setOnPopUp(false);
    }, []);

    /** Aplica o favorito nos dois estados (lista e selecionados) sem refetch. */
    const applyFavorite = useCallback((idProduct: number, favorite: number) => {
        const patch = (list: ProductModel[]) => list.map((item) => (item.idProduct === idProduct ? { ...item, favorite } : item));
        setProducts(patch);
        setSelectedProduct((prev) => (prev.idProduct === idProduct ? { ...prev, favorite } : prev));
        setSelectedProducts((prev) => {
            const next = patch(prev);
            setQuery({ products: next });
            return next;
        });
    }, []);

    // Otimista: o coração muda na hora e só volta se a API falhar. Antes abria
    // o overlay de Loading e refazia o GET /products inteiro a cada clique.
    const handleFavorite = useCallback(
        async (active: ProductModel) => {
            if (!active.idProduct) return;
            // Lê o estado atual da lista, não o objeto recebido (o drawer guarda uma cópia).
            const current = productsRef.current.find((item) => item.idProduct === active.idProduct) ?? active;
            const before = current.favorite ? 1 : 0;
            const after = before ? 0 : 1;
            applyFavorite(active.idProduct, after);

            const [, error] = before
                ? await handlePromise(unFavoriteProduct(idUser, active.idProduct))
                : await handlePromise(favoriteProduct({ id_Product: active.idProduct, id_User: idUser }));

            if (error) {
                applyFavorite(active.idProduct, before);
                setSnackbarMessage(`Erro ao ${before ? "desfavoritar" : "favoritar"}`);
                setShowSnackbar(true);
            }
        },
        [applyFavorite, idUser]
    );

    const clearAll = useCallback(() => commitSelection([]), [commitSelection]);

    const favorites = useMemo(() => products.filter((prod) => prod.favorite === 1), [products]);

    /** Itens da aba ativa, filtrados e ordenados por nome. Memoizado: filtrar e
     *  ordenar centenas de ativos a cada clique de seleção era parte do lag. */
    const visibleProducts = useMemo(() => {
        const source = [products, favorites, selectedProducts][value] ?? products;
        return orderObjectArray(flexMultiFilter(source, [filterKey], filter)).by("productName").asc();
    }, [products, favorites, selectedProducts, value, filterKey, filter]);

    const selectedIds = useMemo(() => new Set(selectedProducts.map((prod) => prod.idProduct)), [selectedProducts]);
    const selectedCount = selectedProducts.length;

    return {
        loading,
        showSnackbar,
        setShowSnackbar,
        snackbarMessage,
        value,
        open,
        setOpen,
        products,
        filter,
        setFilter,
        filterKey,
        setFilterKey,
        selectedProducts,
        selectedProduct,
        annotationOpen,
        setAnnotationOpen,
        activeSelected,
        onPopUp,
        favorites,
        selectedCount,
        selectedIds,
        clearAll,
        visibleProducts,
        handleChange,
        handleClickProduct,
        handleSelectProduct,
        handleAddMoreItems,
        handleContinue,
        handleClickOpen,
        saveAnnotation,
        popUpOpen,
        handleClose,
        handleFavorite,
    };
}
